import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import "./selfPacedUserHeader.scss";
import {
  RootStore,
  useAppDispatch,
  useAppSelector,
} from "../../../../../store/store";
import {
  closeSelfPacedUserEditModal,
  searchSelfPacedUser,
} from "../../../../../store/features/AdminPanel/SelfPacedUser/SelfPacedUserSlice";
import { REQUIRED_VALIDATION } from "../../../../../helpers/utils/formUtils";
import {
  addSelfpacedUser,
  loadSelfPacedUser,
  rensedInvitationForSelfPacedUser,
  updateSelfPacedUser,
} from "../../../../../store/features/AdminPanel/SelfPacedUser/SelfPacedUserApi";
import TableHeadingTitle from "../../../../../shared/TableHeadingTitle/TableHeadingTitle";
import SearchBar from "../../../../../shared/SearchBar/SearchBar";
import { roles } from "../../../../../helpers/utils/Constants/roles";
import { PrimaryButton } from "../../../../../shared/Buttons/Buttons";
import Modal, {
  ModalBody,
  ModalHeader,
} from "../../../../../shared/Modals/Modal";
import TextField from "../../../../../shared/Inputs/TextField/TextField";
import CheckBox from "../../../../../shared/Inputs/CheckBox/CheckBox";
import { SELF_PACED_ROLES } from "../../../../../helpers/utils/Constants/course";

const SelfPacedUserHeader = (props: any) => {
  const { type, rolesToShow } = props;
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([
    SELF_PACED_ROLES[0].value,
  ]);
  const [isUser, setIsUser] = useState(false);
  const dispatch = useAppDispatch();
  const slefPacedUserState: any = useAppSelector((store: RootStore) => {
    return store.SelfPacedUserSlice;
  });
  const authState: any = useAppSelector((store: RootStore) => {
    return store.AuthSlice;
  });

  useEffect(() => {
    dispatch(searchSelfPacedUser(searchText));
  }, [searchText]);

  const validationSchema = yup.object().shape({
    first_name: yup.string().required(REQUIRED_VALIDATION("First Name")),
    last_name: yup.string().required(REQUIRED_VALIDATION("Last Name")),
    email: yup
      .string()
      .required(REQUIRED_VALIDATION("Email"))
      .email("Email is invalid"),
  });

  const formOptions: any = {
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
    reValidateMode: "onClick",
  };

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors },
  } = useForm<any>(formOptions);

  const handleModal = () => {
    reset();
    setShowModal(false);
    dispatch(closeSelfPacedUserEditModal(false));
  };

  const openModal = () => {
    setShowModal(true);
    // setShowEditModal(false);
  };

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  const onSubmit = (values: any) => {
    if (selectedRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }

    const data = {
      ...values,
    };
    if (isUser) {
      data.role = ["user", ...selectedRoles];
    } else {
      data.role = selectedRoles;
    }
    if (slefPacedUserState.editUser) {
      dispatch(
        updateSelfPacedUser({
          userId: slefPacedUserState.single_user_info.id,
          data,
        })
      ).then(() => {
        dispatch(
          loadSelfPacedUser({
            page: slefPacedUserState?.current_page,
            query: rolesToShow,
          })
        );
      });
    } else {
      dispatch(addSelfpacedUser(data)).then(() => {
        dispatch(
          loadSelfPacedUser({
            page: slefPacedUserState?.current_page,
            query: rolesToShow,
          })
        );
      });
    }
  };
  const onError = (error: any) => {
    console.log("ERROR:::", error);
  };

  useEffect(() => {
    if (!slefPacedUserState.adderLoading && slefPacedUserState.edit_success) {
      setShowModal(false);
      dispatch(closeSelfPacedUserEditModal(false));

      reset();
      setSelectedRoles([]);
    }
    if (!slefPacedUserState.adderLoading && slefPacedUserState.add_success) {
      setShowModal(false);
      reset();
      setSelectedRoles([]);
    }
  }, [slefPacedUserState]);

  useEffect(() => {
    if (slefPacedUserState.editUser) {
      setShowModal(true);
      setValue("first_name", slefPacedUserState?.single_user_info?.first_name);
      setValue("last_name", slefPacedUserState?.single_user_info?.last_name);
      setValue("email", slefPacedUserState?.single_user_info?.email);
      // For backward compatibility, check both single role and roles array
      if (slefPacedUserState?.single_user_info?.all_roles.includes("user")) {
        setIsUser(true);
      }

      setSelectedRoles(slefPacedUserState?.single_user_info?.all_roles);
    }
  }, [slefPacedUserState?.editUser, slefPacedUserState?.single_user_info]);

  return (
    <div className="subadmin__header__container">
      <div className="left__section">
        <TableHeadingTitle>Self Paced User</TableHeadingTitle>
      </div>
      <div className="right__section">
        <div className="search">
          <SearchBar value={searchText} setValue={setSearchText} />
        </div>

        {(authState.tac_user?.role === roles.SYSTEM_ADMIN ||
          authState.tac_user?.role === roles.SUB_ADMIN) && (
          <div className="subadmin__button__witdh">
            <PrimaryButton
              text="Add New"
              size="sm"
              type="button"
              onClick={() => openModal()}
            />
          </div>
        )}
      </div>
      <Modal
        show={showModal}
        setShow={setShowModal}
        resetForm={reset}
        onClick={handleModal}
      >
        <ModalHeader
          title={slefPacedUserState.editUser ? "Edit User" : "Add User"}
        />

        <ModalBody>
          <form
            className="user__form__container"
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <div className="flex">
              <div className="sm_width">
                <TextField
                  type="text"
                  label="First Name"
                  name="first_name"
                  register={register}
                  error={errors?.first_name}
                />
              </div>
              <div className="sm_width ">
                <TextField
                  type="text"
                  label="Last Name"
                  name="last_name"
                  register={register}
                  error={errors?.last_name}
                />
              </div>
            </div>

            <TextField
              type="text"
              label="Email Address"
              name="email"
              register={register}
              disabled={slefPacedUserState.editUser}
              error={errors?.email}
            />
            <div className="role-selection">
              <div className="role-label">Select Roles</div>
              <div className="radio-group">
                {SELF_PACED_ROLES.map((role) => (
                  <CheckBox
                    key={role.id}
                    label={role.name}
                    fullwidth
                    value={selectedRoles?.includes(role.value)}
                    onClick={() => toggleRole(role.value)}
                  />
                ))}
              </div>
              {/* {selectedRoles?.length === 0 && (
                <div className="error-message">
                  Please select at least one role
                </div>
              )} */}
            </div>
            <div className="modal_footer">
              {!slefPacedUserState.single_user_info?.last_active &&
                !slefPacedUserState.resend_invitaiton_loading &&
                slefPacedUserState.editUser && (
                  <span
                    role="button"
                    aria-hidden
                    className="resend__invitation"
                    onClick={() =>
                      dispatch(
                        rensedInvitationForSelfPacedUser({
                          email: slefPacedUserState.single_user_info.email,
                        })
                      )
                    }
                  >
                    Resend Invitation
                  </span>
                )}
              {slefPacedUserState.resend_invitaiton_loading && (
                <div className="spinner spinner-margin" />
              )}
              <div>
                <PrimaryButton
                  text={slefPacedUserState.editUser ? "Update" : "Add"}
                  type="submit"
                  loading={slefPacedUserState.adderLoading}
                />
              </div>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default SelfPacedUserHeader;
